import React, { useState, useRef } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import { useAriaKeyboardNavigation } from '@hooks/a11y/use-aria-keyboard-navigation';
import useIsWebview from '@hooks/use-is-webview';
import ConfirmDialog from '../confirm-dialog/confirm-dialog';
import style from './editing.module.scss';

interface UniverseTabProps {
  value: number;
  onChange: (val: number) => void;
}

const data = ['PERSO.EDIT_FILTER_WOMEN.CTA', 'PERSO.EDIT_FILTER_MEN.CTA', 'PERSO.EDIT_FILTER_BOTH.CTA'];

const UniverseTab: React.FC<UniverseTabProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const valRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const isWebview = useIsWebview();

  const handleUniverseChange = (val: number) => {
    valRef.current = val;
    if (val === value) {
      return;
    }
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleConfirm = () => {
    setOpen(false);
    if (valRef.current !== null) {
      onChange(valRef.current);
    }
  };

  // Use keyboard navigation hook with tabs pattern
  const { containerProps, getItemProps } = useAriaKeyboardNavigation({
    pattern: 'tabs',
    direction: 'horizontal',
    itemsRef: tabRefs,
    totalItems: data.length,
    selectedIndex: value,
    onSelect: handleUniverseChange,
    autoActivate: false, // Require Enter/Space to activate (shows confirm dialog)
    loop: true,
    idPrefix: 'universe-tab',
  });

  return (
    <>
      <h3 className={style.universe__title}>{t('PERSO.FILTER_NOT_SET.TITLE')}</h3>
      <div
        className={style.universe__list}
        {...containerProps}
        aria-label="Universe selection"
      >
        {data.map((item, index) => {
          const isSelected = value === index;
          const itemProps = getItemProps(index);

          return (
            <div
              className={style.universe__list__btnWrap}
              key={index}
            >
              <button
                {...itemProps}
                onClick={() => handleUniverseChange(index)}
                className={clsx(
                  style.universe__list__btnTab,
                  isSelected ? style['universe__list__btnTab--checked'] : style['universe__list__btnTab--unchecked'],
                  'vc-btn vc-btn--outline vc-btn--medium',
                )}
                type="button"
              >
                {t(item)}
              </button>
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        setOpen={setOpen}
        open={open}
        title={t('PLP.MYSIZES_DIALOG.CHANGE_CATEGORY.TITLE')}
        content={t('PLP.MYSIZES_DIALOG.CHANGE_CATEGORY.CONTENT')}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        focusTrapActive={!isWebview}
      />
    </>
  );
};

export default UniverseTab;
